import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Home, PlusCircle, LayoutDashboard, LogOut, Building2 } from "lucide-react";

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navLink = (to, label) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
        className={`text-sm font-medium transition-colors ${
          active ? "text-navy" : "text-slate-600 hover:text-navy"
        }`}
      >
        {label}
      </Link>
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight text-slate-900">
              VYAPAAR<span className="text-gold-dark">.</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLink("/discover", "Discover ✨")}
            {navLink("/properties?type=buy", "Buy")}
            {navLink("/properties?type=rent", "Rent")}
            {navLink("/chats", "Chats")}
            {navLink("/post", "Post Property")}
          </div>

          <div className="flex items-center gap-3">
            <Button
              data-testid="nav-post-btn"
              onClick={() => navigate("/post")}
              variant="outline"
              className="hidden sm:flex border-slate-300 text-slate-700 hover:border-navy hover:text-navy"
            >
              <PlusCircle className="h-4 w-4 mr-1.5" /> Post Property
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn" className="focus:outline-none">
                    <Avatar className="h-9 w-9 border-2 border-navy">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-navy text-white text-sm">
                        {user.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-home" onClick={() => navigate("/")}>
                    <Home className="h-4 w-4 mr-2" /> Home
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={handleLogout} className="text-gold-dark">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid="login-btn"
                onClick={login}
                className="bg-navy hover:bg-navy-dark text-white"
              >
                Sign in with Google
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
